/**
 * Sprint 182 — Auth Drift Banner.
 *
 * Listens for the `hibff:auth-drift` custom event dispatched by apiClient
 * when a request to /api/admin/* or /api/vault/* returns 401 despite a
 * recent successful /api/users/me. Surfaces a sticky orange banner with a
 * one-click re-sign-in CTA — so the master admin never silently stares at
 * empty Command Centre tables again.
 */
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';

export const AuthDriftBanner = () => {
  const [drift, setDrift] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setDrift(e?.detail || { url: 'unknown' });
    };
    window.addEventListener('hibff:auth-drift', handler);
    return () => window.removeEventListener('hibff:auth-drift', handler);
  }, []);

  if (!drift) return null;

  return (
    <div
      data-testid="auth-drift-banner"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 60,
        background: 'linear-gradient(90deg,#F5831F 0%,#E5721A 100%)',
        color: 'white',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <AlertTriangle size={16} />
        <span>
          Session drift detected — your admin token is missing on
          <code style={{ background: 'rgba(0,0,0,0.18)', padding: '1px 6px', borderRadius: 4, margin: '0 4px' }}>
            {(drift.url || '').replace(/^.*\/api\//, '/api/')}
          </code>
          . Re-sign in to restore data access.
        </span>
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <a
          href="/api/auth/admin-direct"
          data-testid="auth-drift-resign"
          style={{
            background: 'white',
            color: '#8C3A20',
            padding: '6px 14px',
            borderRadius: 6,
            textDecoration: 'none',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.02em',
          }}
        >
          Re-sign in
        </a>
        <button
          type="button"
          onClick={() => setDrift(null)}
          data-testid="auth-drift-dismiss"
          aria-label="Dismiss session drift banner"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <X size={16} />
        </button>
      </span>
    </div>
  );
};

export default AuthDriftBanner;
